// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useCursorPages } from '@/lib/useCursorPages';
import type { GrantableIntegrationSummary } from '@greenlight/shared';

export const AVAILABLE_INTEGRATIONS_PREVIEW = 5;

function IntegrationChip({ item }: { item: GrantableIntegrationSummary }): React.ReactElement {
  return (
    <li className="inline-flex min-h-11 items-center gap-2 rounded-gl border border-border bg-muted/40 px-2.5 py-1.5 md:min-h-0">
      <IntegrationTile
        slug={item.integration}
        catalogKey={item.catalog_key}
        displayName={item.name}
        size={22}
      />
      <span className="text-sm font-medium text-foreground">{item.name}</span>
    </li>
  );
}

/**
 * Citizen-dev prompt on `/apps`: the org's available integrations, as names
 * and logos the builder agent can be asked to use. IT already has
 * `/integrations`; this surface is for the `user` role only.
 */
export function AvailableIntegrations(): React.ReactElement | null {
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [expanded, setExpanded] = React.useState(false);
  const queryKey = ['integrations-grantable', cursor ?? null] as const;
  const query = api.integrations.grantable.useQuery(
    queryKey,
    { query: { limit: 100, ...(cursor ? { cursor } : {}) } },
    { queryKey },
  );
  const pages = useCursorPages<GrantableIntegrationSummary>({
    query,
    cursor,
    onCursorChange: setCursor,
    getKey: (item) => item.integration,
    autoLoadWhile: () => true,
    invalidateKey: ['integrations-grantable'],
  });

  const items = pages.items;
  const preview = items.slice(0, AVAILABLE_INTEGRATIONS_PREVIEW);
  const rest = items.slice(AVAILABLE_INTEGRATIONS_PREVIEW);

  if (pages.isLoadingFirstPage) {
    return (
      <section
        className="rounded-gl-lg border border-border bg-card p-4 md:p-5"
        aria-labelledby="available-integrations-heading"
        data-testid="available-integrations"
      >
        <h2
          id="available-integrations-heading"
          className="text-lg font-bold tracking-tight text-foreground"
        >
          Ask your agent to build with these
        </h2>
        <div className="mt-4 flex flex-wrap gap-2" aria-hidden="true">
          {Array.from({ length: AVAILABLE_INTEGRATIONS_PREVIEW }, (_, i) => (
            <Skeleton key={i} className="h-11 w-28 rounded-gl md:h-9" />
          ))}
        </div>
      </section>
    );
  }

  if (pages.hasError) {
    return (
      <Alert variant="destructive" data-testid="available-integrations">
        <AlertTitle>Couldn&apos;t load integrations</AlertTitle>
        <AlertDescription>
          The apps list is still available. Refresh to try loading integrations again.
        </AlertDescription>
      </Alert>
    );
  }

  if (items.length === 0) return null;

  return (
    <section
      className="rounded-gl-lg border border-border bg-card p-4 md:p-5"
      aria-labelledby="available-integrations-heading"
      data-testid="available-integrations"
    >
      <h2
        id="available-integrations-heading"
        className="text-lg font-bold tracking-tight text-foreground"
      >
        Ask your agent to build with these
      </h2>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
        Your company has set these integrations up for you. Name them when you ask your agent to
        build.
      </p>
      <ul id="available-integrations-list" className="mt-4 flex flex-wrap gap-2">
        {preview.map((item) => (
          <IntegrationChip key={item.integration} item={item} />
        ))}
        {expanded && rest.map((item) => <IntegrationChip key={item.integration} item={item} />)}
      </ul>
      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls="available-integrations-list"
          className="mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-gl px-1 text-[12.5px] font-medium text-link transition-colors hover:text-link/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-0 md:py-1"
        >
          {expanded ? (
            <>
              Show fewer
              <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
            </>
          ) : (
            <>
              View {rest.length} more
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </>
          )}
        </button>
      )}
    </section>
  );
}
